import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';

import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {MaskedInput} from 'presentation/components/MaskedInput';
import {StyledFixedFooter} from 'presentation/pages/public/styles';
import {TextInput} from 'presentation/components';

import {masks} from 'presentation/utils';
import {Loading} from 'presentation/components/Loading/Loading';
import {useCheckPersonalFields} from 'presentation/pages/public/hooks/useCheckPersonalFields';
import {IFormData, formSchema} from './schema';

const {formLabels, formPlaceholders} = translations['pt-br'].publicFlow;
const {commons} = translations['pt-br'];

export function Step2Form() {
	const methods = useForm<IFormData>({
		defaultValues: {
			birthday: undefined,
			email: '',
			confirmEmail: '',
			cellphone: '',
			confirmCellphone: '',
		},
		resolver: zodResolver(formSchema),
		mode: 'onChange',
		delayError: 1000,
	});

	const {
		handleSubmit,
		register,
		trigger,
		getFieldState,
		formState: {errors, isSubmitting, isValid},
	} = methods;

	const {handleCheckPersonalData, showAPIError, apiErrorMsg} = useCheckPersonalFields({methods});

	const {invalid: invalidBirthday, isDirty: dirtyBirthday} = getFieldState('birthday');
	const {invalid: invalidCellphone, isDirty: dirtyCellphone} = getFieldState('cellphone');
	const {invalid: invalidConfirmCellphone, isDirty: dirtyConfirmCellphone} =
		getFieldState('confirmCellphone');

	return (
		<FormProvider {...methods}>
			<Stack component="form" gap={2} onSubmit={handleSubmit(handleCheckPersonalData)}>
				<Stack>
					<Typography variant="label" component="label" htmlFor="birthday">
						{formLabels.birthday}
					</Typography>
					<MaskedInput
						{...register('birthday')}
						id="birthday"
						maskOptions={masks.birthday}
						autoComplete="current-birthday"
						error={dirtyBirthday && invalidBirthday}
						helperText={dirtyBirthday ? errors?.birthday?.message : undefined}
						label={formPlaceholders.birthday}
						$fieldName="birthday"
					/>
				</Stack>

				<Stack>
					<Typography variant="label" component="label" htmlFor="email">
						{formLabels.email}
					</Typography>
					<Stack gap={2}>
						<TextInput
							name="email"
							inputMode="email"
							slotProps={{input: {onBlur: () => trigger('confirmEmail')}}}
							autoComplete="current-email"
							label={formPlaceholders.email}
						/>
						<TextInput
							onFocus={() => trigger('confirmEmail')}
							name="confirmEmail"
							inputMode="email"
							autoComplete="current-email"
							label={formPlaceholders.confirmEmail}
							onPaste={e => e.preventDefault()}
						/>
					</Stack>
				</Stack>

				<Stack>
					<Typography variant="label" component="label" htmlFor="cellphone">
						{formLabels.cellphone}
					</Typography>
					<Stack gap={2}>
						<MaskedInput
							{...register('cellphone')}
							id="cellphone"
							inputMode="tel"
							slotProps={{input: {onBlur: () => trigger('confirmCellphone')}}}
							maskOptions={masks.cellphone}
							autoComplete="current-cellphone"
							error={dirtyCellphone && invalidCellphone}
							helperText={dirtyCellphone ? errors.cellphone?.message : undefined}
							label={formPlaceholders.cellphone}
							$fieldName="cellphone"
						/>
						<MaskedInput
							{...register('confirmCellphone')}
							onFocus={() => trigger('confirmCellphone')}
							id="confirmCellphone"
							inputMode="tel"
							maskOptions={masks.cellphone}
							autoComplete="current-cellphone"
							error={dirtyConfirmCellphone && invalidConfirmCellphone}
							helperText={
								dirtyConfirmCellphone ? errors.confirmCellphone?.message : undefined
							}
							label={formPlaceholders.confirmCellphone}
							$fieldName="confirmCellphone"
							onPaste={e => e.preventDefault()}
						/>
					</Stack>
				</Stack>
				{showAPIError ? <Alert severity="error">{apiErrorMsg}</Alert> : null}
				<StyledFixedFooter>
					<Button
						fullWidth
						size="large"
						disabled={!isValid || isSubmitting}
						type="submit"
						endIcon={
							isSubmitting ? (
								<Loading color="inherit" size={16} />
							) : (
								<Icon name="arrow-right-filled" />
							)
						}>
						{commons.keep}
					</Button>
				</StyledFixedFooter>
			</Stack>
		</FormProvider>
	);
}
